"use client";

// import type { Animal } from "@/lib/types";

import { useState } from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown, Info, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Animal } from "@/features/Animals/AnimalColumns.ts";
import { Button } from "@heroui/button";
import { useAnimalDashboardTour } from "@/features/tour/officials.tour.ts";

export function AnimalDataTable({ animals }: { animals: Animal[] }) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const { startTour } = useAnimalDashboardTour();

  const columns: ColumnDef<Animal>[] = [
    {
      accessorKey: "gn",
      header: ({ column }) => {
        return (
          <Button
            variant="light"
            size="sm"
            className={"text-[#cfc130ff]"}
            onPress={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            DGR
            <ArrowUpDown className="ml-2 h-4 w-4 " />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("gn")}</div>
      ),
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            size="sm"
            variant="light"
            className={"text-[#cfc130ff]"}
            onPress={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("name")}</div>
      ),
    },
    // {
    // 	accessorKey: "_id",
    // 	header: "ID",
    // 	cell: ({ row }) => <div>{row.getValue("_id")}</div>,
    // },
    {
      accessorKey: "sex",
      header: "Sex",
      cell: ({ row }) => <div>{row.getValue("sex")}</div>,
    },
    {
      accessorKey: "age",
      header: ({ column }) => {
        return (
          <Button
            size="sm"
            variant="light"
            className={"text-[#cfc130ff]"}
            onPress={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Age
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const age = Number.parseFloat(row.getValue("age"));

        return <div>{age} months</div>;
      },
    },
    {
      accessorKey: "weight",
      header: ({ column }) => {
        return (
          <Button
            variant="light"
            size="sm"
            className={"text-[#cfc130ff]"}
            onPress={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Weight
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const weight = Number.parseFloat(row.getValue("weight"));

        return <div>{weight.toFixed(1)} kg</div>;
      },
    },
    {
      accessorKey: "animalStatus",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("animalStatus") as string;

        return (
          <Badge variant={status === "active" ? "default" : "destructive"}>
            {status}
          </Badge>
        );
      },
    },
    // {
    // 	accessorKey: "farm",
    // 	header: "Farm",
    // 	cell: ({ row }) => <div className='capitalize'>{row.getValue("farm")}</div>,
    // },
    {
      id: "actions",
      cell: ({ row }) => {
        const animal = row.original;

        return (
          <Button
            id="viewAnimalProfileButton"
            variant="flat"
            size="sm"
            color="primary"
            onPress={() => navigate(`/animals/${animal?._id}`)}
          >
            View Details
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data: animals,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  return (
    <div className="w-full mx-auto justify-center items-center space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-full flex flex-row justify-between relative w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search animals..."
              value={
                (table.getColumn("name")?.getFilterValue() as string) ?? ""
              }
              onChange={(event) =>
                table.getColumn("name")?.setFilterValue(event.target.value)
              }
            />
          </div>
          <div className="flex flex-row gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="ml-auto" variant="light">
                  Columns <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        checked={column.getIsVisible()}
                        className="capitalize"
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            isIconOnly
            size="sm"
            color="primary"
            variant="flat"
            // className="absolute top-4 right-4 z-30"
            className={"z-30 opacity-10"}
            startContent={<Info />}
            onPress={startTour}
          />
        </div>
      </div>
      <div className="rounded-md border">
        <Table id={"animalTableCard"} className={"rounded-md"}>
          <TableHeader
            className={'bg-[#015354ff] text-[#cfc130ff] rounded-md"}'}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className={"text-[#cfc130ff]"}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  className="h-24 text-center"
                  colSpan={columns.length}
                >
                  No animals found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </div>
        <Button
          disabled={!table.getCanPreviousPage()}
          size="sm"
          variant="flat"
          onClick={() => table.previousPage()}
        >
          Previous
        </Button>
        <Button
          disabled={!table.getCanNextPage()}
          size="sm"
          variant="flat"
          onClick={() => table.nextPage()}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
